import React, { createContext, useContext, useReducer, useEffect } from "react";
import { AuthContext } from "../Auth/AuthContext";
import { SocketContext } from "../Socket/SocketContext";
import api from "../../services/api";
import toastUtils from "../../utils/toastUtils";
import { i18n } from "../../translate/i18n";

const AsteriskContext = createContext();

// Estados iniciais
const initialState = {
  // Conexões
  connections: [],
  selectedConnection: null,
  connectionStatus: "idle", // idle, connecting, connected, error

  // Endpoints
  endpoints: [],
  selectedEndpoint: null,

  // Chamadas
  calls: [],
  currentCall: null,
  callStatus: "idle", // idle, calling, ringing, connected, onhold, muted
  callHistory: [],

  // Estados da interface
  phoneDialogOpen: false,
  historyDialogOpen: false,
  settingsDialogOpen: false,

  // Estados de chamada
  isInCall: false,
  isMuted: false,
  isOnHold: false,
  callDuration: 0,
  incomingCall: null,

  // Configurações
  autoAnswer: false,
  ringVolume: 80,
  micVolume: 80,
  speakerVolume: 80,

  // Estado de carregamento
  loading: false,
  error: null,

  // Estatísticas
  stats: {
    totalConnections: 0,
    connectedConnections: 0,
    totalCalls: 0,
    activeCalls: 0,
    callsToday: 0,
    missedCalls: 0
  }
};

// Actions
const ACTIONS = {
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",

  // Conexões
  SET_CONNECTIONS: "SET_CONNECTIONS",
  ADD_CONNECTION: "ADD_CONNECTION",
  UPDATE_CONNECTION: "UPDATE_CONNECTION",
  REMOVE_CONNECTION: "REMOVE_CONNECTION",
  SET_SELECTED_CONNECTION: "SET_SELECTED_CONNECTION",
  SET_CONNECTION_STATUS: "SET_CONNECTION_STATUS",

  // Endpoints
  SET_ENDPOINTS: "SET_ENDPOINTS",
  ADD_ENDPOINT: "ADD_ENDPOINT",
  UPDATE_ENDPOINT: "UPDATE_ENDPOINT",
  REMOVE_ENDPOINT: "REMOVE_ENDPOINT",
  SET_SELECTED_ENDPOINT: "SET_SELECTED_ENDPOINT",

  // Chamadas
  SET_CALLS: "SET_CALLS",
  ADD_CALL: "ADD_CALL",
  UPDATE_CALL: "UPDATE_CALL",
  REMOVE_CALL: "REMOVE_CALL",
  SET_CURRENT_CALL: "SET_CURRENT_CALL",
  SET_CALL_STATUS: "SET_CALL_STATUS",
  SET_CALL_HISTORY: "SET_CALL_HISTORY",

  // Estados de chamada
  SET_IN_CALL: "SET_IN_CALL",
  SET_MUTED: "SET_MUTED",
  SET_ON_HOLD: "SET_ON_HOLD",
  SET_CALL_DURATION: "SET_CALL_DURATION",
  SET_INCOMING_CALL: "SET_INCOMING_CALL",

  // Interface
  SET_PHONE_DIALOG: "SET_PHONE_DIALOG",
  SET_HISTORY_DIALOG: "SET_HISTORY_DIALOG",
  SET_SETTINGS_DIALOG: "SET_SETTINGS_DIALOG",

  // Configurações
  SET_AUTO_ANSWER: "SET_AUTO_ANSWER",
  SET_RING_VOLUME: "SET_RING_VOLUME",
  SET_MIC_VOLUME: "SET_MIC_VOLUME",
  SET_SPEAKER_VOLUME: "SET_SPEAKER_VOLUME",

  // Estatísticas
  SET_STATS: "SET_STATS",
  UPDATE_STATS: "UPDATE_STATS",

  // Reset
  RESET_CALL_STATE: "RESET_CALL_STATE",
  RESET_ALL: "RESET_ALL"
};

// Reducer
const asteriskReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };

    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };

    case ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };

    // Conexões
    case ACTIONS.SET_CONNECTIONS:
      return { ...state, connections: action.payload };

    case ACTIONS.ADD_CONNECTION:
      return {
        ...state,
        connections: [...state.connections, action.payload]
      };

    case ACTIONS.UPDATE_CONNECTION:
      return {
        ...state,
        connections: state.connections.map(conn =>
          conn.id === action.payload.id ? action.payload : conn
        )
      };

    case ACTIONS.REMOVE_CONNECTION:
      return {
        ...state,
        connections: state.connections.filter(conn => conn.id !== action.payload)
      };

    case ACTIONS.SET_SELECTED_CONNECTION:
      return { ...state, selectedConnection: action.payload };

    case ACTIONS.SET_CONNECTION_STATUS:
      return { ...state, connectionStatus: action.payload };

    // Endpoints
    case ACTIONS.SET_ENDPOINTS:
      return { ...state, endpoints: action.payload };

    case ACTIONS.ADD_ENDPOINT:
      return {
        ...state,
        endpoints: [...state.endpoints, action.payload]
      };

    case ACTIONS.UPDATE_ENDPOINT:
      return {
        ...state,
        endpoints: state.endpoints.map(endpoint =>
          endpoint.id === action.payload.id ? action.payload : endpoint
        )
      };

    case ACTIONS.REMOVE_ENDPOINT:
      return {
        ...state,
        endpoints: state.endpoints.filter(endpoint => endpoint.id !== action.payload)
      };

    case ACTIONS.SET_SELECTED_ENDPOINT:
      return { ...state, selectedEndpoint: action.payload };

    // Chamadas
    case ACTIONS.SET_CALLS:
      return { ...state, calls: action.payload };

    case ACTIONS.ADD_CALL:
      return {
        ...state,
        calls: [...state.calls, action.payload]
      };

    case ACTIONS.UPDATE_CALL:
      return {
        ...state,
        calls: state.calls.map(call =>
          call.id === action.payload.id ? action.payload : call
        )
      };

    case ACTIONS.REMOVE_CALL:
      return {
        ...state,
        calls: state.calls.filter(call => call.id !== action.payload)
      };

    case ACTIONS.SET_CURRENT_CALL:
      return { ...state, currentCall: action.payload };

    case ACTIONS.SET_CALL_STATUS:
      return { ...state, callStatus: action.payload };

    case ACTIONS.SET_CALL_HISTORY:
      return { ...state, callHistory: action.payload };

    // Estados de chamada
    case ACTIONS.SET_IN_CALL:
      return { ...state, isInCall: action.payload };

    case ACTIONS.SET_MUTED:
      return { ...state, isMuted: action.payload };

    case ACTIONS.SET_ON_HOLD:
      return { ...state, isOnHold: action.payload };

    case ACTIONS.SET_CALL_DURATION:
      return { ...state, callDuration: action.payload };

    case ACTIONS.SET_INCOMING_CALL:
      return { ...state, incomingCall: action.payload };

    // Interface
    case ACTIONS.SET_PHONE_DIALOG:
      return { ...state, phoneDialogOpen: action.payload };

    case ACTIONS.SET_HISTORY_DIALOG:
      return { ...state, historyDialogOpen: action.payload };

    case ACTIONS.SET_SETTINGS_DIALOG:
      return { ...state, settingsDialogOpen: action.payload };

    // Configurações
    case ACTIONS.SET_AUTO_ANSWER:
      return { ...state, autoAnswer: action.payload };

    case ACTIONS.SET_RING_VOLUME:
      return { ...state, ringVolume: action.payload };

    case ACTIONS.SET_MIC_VOLUME:
      return { ...state, micVolume: action.payload };

    case ACTIONS.SET_SPEAKER_VOLUME:
      return { ...state, speakerVolume: action.payload };

    // Estatísticas
    case ACTIONS.SET_STATS:
      return { ...state, stats: action.payload };

    case ACTIONS.UPDATE_STATS:
      return {
        ...state,
        stats: { ...state.stats, ...action.payload }
      };

    // Reset
    case ACTIONS.RESET_CALL_STATE:
      return {
        ...state,
        currentCall: null,
        callStatus: "idle",
        isInCall: false,
        isMuted: false,
        isOnHold: false,
        callDuration: 0,
        incomingCall: null
      };

    case ACTIONS.RESET_ALL:
      return initialState;

    default:
      return state;
  }
};

// Provider
const AsteriskProvider = ({ children }) => {
  const [state, dispatch] = useReducer(asteriskReducer, initialState);
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);

  // Ações para conexões
  const fetchConnections = async (params = {}) => {
    // Verificar se usuário tem permissão antes de fazer a chamada
    if (!user || !user.profile || (user.profile !== "admin" && (!user.company || !user.company.plan || !user.company.plan.useAsterisk))) {
      console.log("Usuário não tem permissão para acessar Asterisk");
      return;
    }

    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      const { data } = await api.get("/asterisk-connections", { params });

      dispatch({ type: ACTIONS.SET_CONNECTIONS, payload: data.asteriskConnections || [] });

      // Atualizar estatísticas
      const connectedCount = data.asteriskConnections?.filter(
        conn => conn.status === "connected"
      ).length || 0;

      dispatch({
        type: ACTIONS.UPDATE_STATS,
        payload: {
          totalConnections: data.asteriskConnections?.length || 0,
          connectedConnections: connectedCount
        }
      });

      // Selecionar primeira conexão ativa se não houver uma selecionada
      if (!state.selectedConnection && data.asteriskConnections?.length > 0) {
        const activeConnection = data.asteriskConnections.find(
          conn => conn.status === "connected" && conn.isActive
        );
        if (activeConnection) {
          dispatch({ type: ACTIONS.SET_SELECTED_CONNECTION, payload: activeConnection });
        }
      }

      return data;
    } catch (err) {
      // Verificar se é um erro de permissão (recurso não disponível)
      if (err?.response?.status === 403) {
        console.warn('Acesso negado às conexões Asterisk - recurso pode não estar disponível no plano atual');
        // Não mostrar toast para erros 403, pois são esperados quando recursos não estão ativos
      } else if (err?.response?.status === 500) {
        console.warn('Erro interno do servidor nas conexões Asterisk');
        // Não mostrar toast para erros 500
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
        toastUtils.error(err);
      }
      return { asteriskConnections: [] };
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  const createConnection = async (connectionData) => {
    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      const { data } = await api.post("/asterisk-connections", connectionData);

      dispatch({ type: ACTIONS.ADD_CONNECTION, payload: data.asteriskConnection });
      toastUtils.success(i18n.t("asterisk.toasts.connectionCreated"));

      return data;
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      toastUtils.error(err);
      throw err;
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  const updateConnection = async (connectionId, connectionData) => {
    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      const { data } = await api.put(`/asterisk-connections/${connectionId}`, connectionData);

      dispatch({ type: ACTIONS.UPDATE_CONNECTION, payload: data.asteriskConnection });
      toastUtils.success(i18n.t("asterisk.toasts.connectionUpdated"));

      return data;
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      toastUtils.error(err);
      throw err;
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  const deleteConnection = async (connectionId) => {
    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      await api.delete(`/asterisk-connections/${connectionId}`);

      dispatch({ type: ACTIONS.REMOVE_CONNECTION, payload: connectionId });
      toastUtils.success(i18n.t("asterisk.toasts.connectionDeleted"));
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      toastUtils.error(err);
      throw err;
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Ações para endpoints
  const fetchEndpoints = async (params = {}) => {
    // Verificar se usuário tem permissão antes de fazer a chamada
    if (!user || !user.profile || (user.profile !== "admin" && (!user.company || !user.company.plan || !user.company.plan.useAsterisk))) {
      console.log("Usuário não tem permissão para acessar Asterisk");
      return;
    }

    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      const { data } = await api.get("/asterisk-endpoints", { params });

      dispatch({ type: ACTIONS.SET_ENDPOINTS, payload: data.asteriskEndpoints || [] });

      return data;
    } catch (err) {
      // Verificar se é um erro de permissão (recurso não disponível)
      if (err?.response?.status === 403) {
        console.warn('Acesso negado aos endpoints Asterisk - recurso pode não estar disponível no plano atual');
        // Não mostrar toast para erros 403, pois são esperados quando recursos não estão ativos
      } else if (err?.response?.status === 500) {
        console.warn('Erro interno do servidor nos endpoints Asterisk');
        // Não mostrar toast para erros 500
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
        toastUtils.error(err);
      }
      return { asteriskEndpoints: [] };
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  const createEndpoint = async (endpointData) => {
    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      const { data } = await api.post("/asterisk-endpoints", endpointData);

      dispatch({ type: ACTIONS.ADD_ENDPOINT, payload: data });
      toastUtils.success(i18n.t("asterisk.toasts.endpointCreated"));

      return data;
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      toastUtils.error(err);
      throw err;
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  const updateEndpoint = async (endpointId, endpointData) => {
    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      const { data } = await api.put(`/asterisk-endpoints/${endpointId}`, endpointData);

      dispatch({ type: ACTIONS.UPDATE_ENDPOINT, payload: data });
      toastUtils.success(i18n.t("asterisk.toasts.endpointUpdated"));

      return data;
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      toastUtils.error(err);
      throw err;
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  const deleteEndpoint = async (endpointId) => {
    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      await api.delete(`/asterisk-endpoints/${endpointId}`);

      dispatch({ type: ACTIONS.REMOVE_ENDPOINT, payload: endpointId });
      toastUtils.success(i18n.t("asterisk.toasts.endpointDeleted"));
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      toastUtils.error(err);
      throw err;
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Ações para chamadas
  const fetchCalls = async (params = {}) => {
    // Verificar se usuário tem permissão antes de fazer a chamada
    if (!user || !user.profile || (user.profile !== "admin" && (!user.company || !user.company.plan || !user.company.plan.useAsterisk))) {
      console.log("Usuário não tem permissão para acessar Asterisk");
      return;
    }

    try {
      const { data } = await api.get("/asterisk-calls", { params });

      dispatch({ type: ACTIONS.SET_CALLS, payload: data.asteriskCalls || [] });

      // Atualizar estatísticas
      const activeCalls = data.asteriskCalls?.filter(
        call => call.status === "active" || call.status === "ringing"
      ).length || 0;

      const today = new Date().toISOString().split('T')[0];
      const callsToday = data.asteriskCalls?.filter(
        call => call.createdAt.startsWith(today)
      ).length || 0;

      const missedCalls = data.asteriskCalls?.filter(
        call => call.status === "missed"
      ).length || 0;

      dispatch({
        type: ACTIONS.UPDATE_STATS,
        payload: {
          totalCalls: data.asteriskCalls?.length || 0,
          activeCalls,
          callsToday,
          missedCalls
        }
      });

      return data;
    } catch (err) {
      // Verificar se é um erro de permissão (recurso não disponível)
      if (err?.response?.status === 403) {
        console.warn('Acesso negado às chamadas Asterisk - recurso pode não estar disponível no plano atual');
        // Não mostrar toast para erros 403, pois são esperados quando recursos não estão ativos
      } else if (err?.response?.status === 500) {
        console.warn('Erro interno do servidor nas chamadas Asterisk');
        // Não mostrar toast para erros 500
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
        toastUtils.error(err);
      }
      return { asteriskCalls: [] };
    }
  };

  const initiateCall = async (callData) => {
    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      const { data } = await api.post("/asterisk-calls/initiate", callData);

      dispatch({ type: ACTIONS.SET_CURRENT_CALL, payload: data.call });
      dispatch({ type: ACTIONS.SET_CALL_STATUS, payload: "calling" });
      dispatch({ type: ACTIONS.SET_IN_CALL, payload: true });

      toastUtils.success(i18n.t("asterisk.toasts.callInitiated"));
      return data;
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      toastUtils.error(err);
      throw err;
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  const answerCall = async (callId) => {
    try {
      await api.post(`/asterisk-calls/${callId}/answer`);
      dispatch({ type: ACTIONS.SET_CALL_STATUS, payload: "connected" });
      dispatch({ type: ACTIONS.SET_INCOMING_CALL, payload: null });
      toastUtils.success(i18n.t("asterisk.toasts.callAnswered"));
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      toastUtils.error(err);
      throw err;
    }
  };

  const hangupCall = async (callId) => {
    try {
      await api.post(`/asterisk-calls/${callId}/hangup`);
      dispatch({ type: ACTIONS.RESET_CALL_STATE });
      toastUtils.success(i18n.t("asterisk.toasts.callEnded"));
      fetchCalls();
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      toastUtils.error(err);
      throw err;
    }
  };

  const getCallHistory = async (params = {}) => {
    // Verificar se usuário tem permissão antes de fazer a chamada
    if (!user || !user.profile || (user.profile !== "admin" && (!user.company || !user.company.plan || !user.company.plan.useAsterisk))) {
      console.log("Usuário não tem permissão para acessar Asterisk");
      return;
    }

    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      const { data } = await api.get("/asterisk-calls", { params });

      dispatch({ type: ACTIONS.SET_CALL_HISTORY, payload: data.asteriskCalls || [] });

      return data;
    } catch (err) {
      // Verificar se é um erro de permissão (recurso não disponível)
      if (err?.response?.status === 403) {
        console.warn('Acesso negado ao histórico de chamadas Asterisk - recurso pode não estar disponível no plano atual');
        // Não mostrar toast para erros 403, pois são esperados quando recursos não estão ativos
      } else if (err?.response?.status === 500) {
        console.warn('Erro interno do servidor no histórico de chamadas Asterisk');
        // Não mostrar toast para erros 500
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
        toastUtils.error(err);
      }
      return { asteriskCalls: [] };
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  const deleteCallHistory = async (callId) => {
    // Verificar se usuário tem permissão antes de fazer a chamada
    if (!user || !user.profile || (user.profile !== "admin" && (!user.company || !user.company.plan || !user.company.plan.useAsterisk))) {
      console.log("Usuário não tem permissão para acessar Asterisk");
      return;
    }

    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      await api.delete(`/asterisk-calls/${callId}`);
      toastUtils.success(i18n.t("asterisk.toasts.callHistoryDeleted"));

      // Atualizar histórico após deletar
      await getCallHistory();
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      toastUtils.error(err);
      throw err;
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  const exportCallHistory = async (params = {}) => {
    // Verificar se usuário tem permissão antes de fazer a chamada
    if (!user || !user.profile || (user.profile !== "admin" && (!user.company || !user.company.plan || !user.company.plan.useAsterisk))) {
      console.log("Usuário não tem permissão para acessar Asterisk");
      return;
    }

    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      const response = await api.post("/asterisk-calls/export", params, {
        responseType: 'blob'
      });

      // Criar URL para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Definir nome do arquivo baseado no formato
      const format = params.format || 'csv';
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `call-history-${timestamp}.${format}`);

      document.body.appendChild(link);
      link.click();
      link.remove();

      toastUtils.success(i18n.t("asterisk.toasts.callHistoryExported"));
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      toastUtils.error(err);
      throw err;
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Configurar listeners do socket
  useEffect(() => {
    if (socketManager && socketManager.getSocket()) {
      const socket = socketManager.getSocket();

      const handleCallInitiated = (data) => {
        dispatch({ type: ACTIONS.SET_CURRENT_CALL, payload: data.call });
        dispatch({ type: ACTIONS.SET_CALL_STATUS, payload: "calling" });
        dispatch({ type: ACTIONS.SET_IN_CALL, payload: true });
        fetchCalls();
      };

      const handleCallAnswered = (data) => {
        dispatch({ type: ACTIONS.SET_CURRENT_CALL, payload: data.call });
        dispatch({ type: ACTIONS.SET_CALL_STATUS, payload: "connected" });
        dispatch({ type: ACTIONS.SET_INCOMING_CALL, payload: null });
        fetchCalls();
      };

      const handleCallHangup = (data) => {
        dispatch({ type: ACTIONS.RESET_CALL_STATE });
        fetchCalls();
      };

      const handleIncomingCall = (data) => {
        dispatch({ type: ACTIONS.SET_INCOMING_CALL, payload: data.call });
        dispatch({ type: ACTIONS.SET_CURRENT_CALL, payload: data.call });
        dispatch({ type: ACTIONS.SET_CALL_STATUS, payload: "incoming" });
        dispatch({ type: ACTIONS.SET_IN_CALL, payload: true });

        // Auto-answer se configurado
        if (state.autoAnswer) {
          setTimeout(() => answerCall(data.call.id), 2000);
        }

        fetchCalls();
      };

      const handleCallStatus = (data) => {
        dispatch({ type: ACTIONS.SET_CALL_STATUS, payload: data.status });
        if (data.call) {
          dispatch({ type: ACTIONS.SET_CURRENT_CALL, payload: data.call });
        }
        fetchCalls();
      };

      const handleConnectionStatus = (data) => {
        fetchConnections();
      };

      // Registrar listeners
      socket.on("asterisk_call_initiated", handleCallInitiated);
      socket.on("asterisk_call_answered", handleCallAnswered);
      socket.on("asterisk_call_hangup", handleCallHangup);
      socket.on("asterisk_incoming_call", handleIncomingCall);
      socket.on("asterisk_call_status", handleCallStatus);
      socket.on("asterisk_connection_status", handleConnectionStatus);

      return () => {
        socket.off("asterisk_call_initiated", handleCallInitiated);
        socket.off("asterisk_call_answered", handleCallAnswered);
        socket.off("asterisk_call_hangup", handleCallHangup);
        socket.off("asterisk_incoming_call", handleIncomingCall);
        socket.off("asterisk_call_status", handleCallStatus);
        socket.off("asterisk_connection_status", handleConnectionStatus);
      };
    }
  }, [socketManager, state.autoAnswer, fetchCalls, fetchConnections]);

  // Timer para duração da chamada
  useEffect(() => {
    let interval;
    if (state.callStatus === "connected" && state.isInCall) {
      interval = setInterval(() => {
        dispatch({
          type: ACTIONS.SET_CALL_DURATION,
          payload: state.callDuration + 1
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [state.callStatus, state.isInCall, state.callDuration]);

  // Carregar dados iniciais - APENAS se o usuário tiver permissão para Asterisk
  useEffect(() => {
    if (user && user.profile && (user.profile === "admin" || (user.company && user.company.plan && user.company.plan.useAsterisk))) {
      fetchConnections();
      fetchCalls();
    }
  }, [user]);

  // Valor do contexto
  const value = {
    ...state,
    dispatch,

    // Ações de conexão
    fetchConnections,
    createConnection,
    updateConnection,
    deleteConnection,

    // Ações de endpoint
    fetchEndpoints,
    createEndpoint,
    updateEndpoint,
    deleteEndpoint,

    // Ações de chamada
    fetchCalls,
    initiateCall,
    answerCall,
    hangupCall,
    getCallHistory,
    deleteCallHistory,
    exportCallHistory,

    // Utilitários
    clearError: () => dispatch({ type: ACTIONS.CLEAR_ERROR }),
    setSelectedConnection: (connection) =>
      dispatch({ type: ACTIONS.SET_SELECTED_CONNECTION, payload: connection }),
    openPhoneDialog: () => dispatch({ type: ACTIONS.SET_PHONE_DIALOG, payload: true }),
    closePhoneDialog: () => dispatch({ type: ACTIONS.SET_PHONE_DIALOG, payload: false }),
    openHistoryDialog: () => dispatch({ type: ACTIONS.SET_HISTORY_DIALOG, payload: true }),
    closeHistoryDialog: () => dispatch({ type: ACTIONS.SET_HISTORY_DIALOG, payload: false }),
    openSettingsDialog: () => dispatch({ type: ACTIONS.SET_SETTINGS_DIALOG, payload: true }),
    closeSettingsDialog: () => dispatch({ type: ACTIONS.SET_SETTINGS_DIALOG, payload: false }),
  };

  return (
    <AsteriskContext.Provider value={value}>
      {children}
    </AsteriskContext.Provider>
  );
};

export { AsteriskContext, AsteriskProvider };